import type { Logger } from '@zg-rentals/logger-browser';

export const LOCAL_STORAGE_KEY_PREFIX = {
  MESSAGE_DRAFT: 'messageDraft-',
  ATTACHMENTS_DRAFT: 'messageDraftAttachments-',
} as const;

/**
 * Safely get an item from localStorage
 * @param key - The localStorage key
 * @param defaultValue - Default value to return if localStorage is not available or key doesn't exist
 * @param logger - Logger instance for error reporting
 * @returns The stored value or defaultValue
 */
export const localStorageSafeGetItem = (key: string, defaultValue = '', logger?: Logger): string => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key) || defaultValue;
    }

    if (logger) {
      logger.warn('localStorage not available');
    }
  } catch (error) {
    if (logger) {
      logger.warn('localStorage.getItem failed:', error);
    }
  }
  return defaultValue;
};

/**
 * Safely set an item in localStorage
 * @param key - The localStorage key
 * @param value - The value to store
 * @param logger - Logger instance for error reporting
 * @returns true if successful, false otherwise
 */
export const localStorageSafeSetItem = (key: string, value: string, logger?: Logger): boolean => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
      return true;
    }

    if (logger) {
      logger.warn('localStorage not available');
    }
  } catch (error) {
    if (logger) {
      logger.warn('localStorage.setItem failed:', error);
    }
  }
  return false;
};

/**
 * Safely remove an item from localStorage
 * @param key - The localStorage key
 * @param logger - Logger instance for error reporting
 * @returns true if successful, false otherwise
 */
export const localStorageSafeRemoveItem = (key: string, logger?: Logger): boolean => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(key);
      return true;
    }

    if (logger) {
      logger.warn('localStorage not available');
    }
  } catch (error) {
    if (logger) {
      logger.warn('localStorage.removeItem failed:', error);
    }
  }
  return false;
};

/**
 * Safely parse attachments from localStorage
 * @param key - The localStorage key
 * @param defaultValue - Default value to return if localStorage is not available or parsing fails
 * @param logger - Logger instance for error reporting
 * @returns The parsed attachments array or defaultValue
 */
export const localStorageSafeParseAttachments = (key: string, defaultValue = [], logger?: Logger) => {
  try {
    const uploadsString = localStorageSafeGetItem(key, '[]', logger);
    const parsedAttachments = JSON.parse(uploadsString);

    if (Array.isArray(parsedAttachments)) {
      return parsedAttachments;
    }

    if (logger) {
      logger.warn('Parsed attachments is not an array:', parsedAttachments);
    }
  } catch (error) {
    if (logger) {
      logger.warn('Failed to parse uploaded attachments from localStorage:', error);
    }
  }

  return defaultValue;
};
